import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUpdateInvoiceInline } from '@/hooks';
import type { Invoice } from '@/lib/schemas';

interface InvoicePoNumberFieldProps {
  invoice: Invoice;
}

/**
 * SPEC-project-po-number-2026-08-17 R2 — the invoice's PO Number field.
 *
 * Rule 1 (copy at creation): `invoice.po_number` already carries the
 * project's PO from the moment the invoice was created (backend copy-down,
 * routers/invoices.py create_invoice) — so "the inherited value visible
 * before she types" (AC8) is simply this field's current value. There is no
 * client-side resolver here, matching the backend's own no-resolver design.
 *
 * Rule 2 (offer to write back, never silently — AC4/4a/4b/4c): saving a NEW,
 * non-empty PO that DIFFERS from the project's current PO
 * (`invoice.project_po_number`) opens an inline confirm dialog at the moment
 * she enters it — not a settings screen. This covers BOTH the project having
 * no PO yet AND the project already having a different (possibly stale) one
 * — iteration 2 (christa-review HIGH po-replacement-never-sticks): the
 * original version only offered the dialog when the project had none, so a
 * genuine replacement PO on a project that already had one never had a way
 * to stick — the next invoice kept silently pre-filling the stale value.
 * Accepting sends `apply_po_to_project: true` alongside `po_number` on the
 * same PUT; declining (or the dialog's own Escape/backdrop dismissal) sends
 * the PUT without it — the invoice change is NOT rolled back on Skip, only
 * the write-back is skipped (Rule 2's "changes only that invoice" default).
 *
 * Editable on ANY invoice status — mirrors the identity-correctable
 * precedent (is_identity_editable, always True): a typo in a sent invoice's
 * PO is a correction, not a locked field. See models/invoices.py's
 * `allowed_fields` comment for the same reasoning on the backend.
 *
 * AC9 ("cleared back to inherited"): pressing Escape while editing reverts
 * the DRAFT input back to the invoice's current stored value (the same
 * cancel-edit interaction InvoiceNotesSection already uses) — it does not
 * save anything. "Cleared" here means cancelling an in-progress edit, not
 * writing null; saving an actual empty value is a separate, deliberate save.
 */
export function InvoicePoNumberField({ invoice }: InvoicePoNumberFieldProps) {
  const updateInvoiceInline = useUpdateInvoiceInline();

  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const [pendingWriteBack, setPendingWriteBack] = useState<{ value: string } | null>(null);

  const currentValue = invoice.po_number ?? '';
  const projectPoValue = invoice.project_po_number ?? '';

  const save = (value: string, applyToProject: boolean) => {
    updateInvoiceInline.mutate({
      id: invoice.id,
      data: {
        po_number: value,
        ...(applyToProject ? { apply_po_to_project: true } : {}),
      },
    });
  };

  const handleBlur = () => {
    const trimmed = draftValue.trim();
    if (trimmed === currentValue) {
      setIsEditing(false);
      return;
    }

    // AC4 (amended iteration 2, christa-review HIGH
    // po-replacement-never-sticks): offer the write-back whenever a NEW,
    // non-empty PO DIFFERS from the project's CURRENT po_number — not only
    // when the project has none. Clearing (trimmed === '') never offers a
    // write-back — there's nothing to propagate. Typing back the SAME value
    // the project already has is a no-op, not an offer.
    if (trimmed && trimmed !== projectPoValue) {
      setPendingWriteBack({ value: trimmed });
      // Leave isEditing true until the dialog resolves, so Escape-cancel on
      // the dialog itself can still fall through to the field's own cancel.
      return;
    }

    save(trimmed, false);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      // AC9: revert the draft back to the invoice's current value — a
      // cancelled edit, not a save.
      setDraftValue(currentValue);
      setIsEditing(false);
    }
  };

  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
        PO Number
      </p>
      <div
        className="bg-blue-50 border border-blue-100 rounded p-3 hover:bg-blue-100 transition-colors cursor-pointer"
        onClick={() => {
          if (!isEditing) {
            setDraftValue(currentValue);
            setIsEditing(true);
          }
        }}
      >
        {isEditing ? (
          <Input
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            placeholder="e.g., PO-2026-118"
            disabled={updateInvoiceInline.isPending}
            className="text-sm"
            data-testid="invoice-po-number-input"
          />
        ) : (
          <p
            className={currentValue ? 'text-sm flex items-center gap-2' : 'text-sm text-muted-foreground italic flex items-center gap-2'}
            data-testid="invoice-po-number-display"
          >
            {currentValue || 'No PO (click to add)'}
            <Pencil className="h-3 w-3 text-muted-foreground" />
          </p>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        The client&apos;s own purchase order reference — printed on the invoice PDF.
      </p>

      {/* AC4/4a/4b/4c: presented right here, at the moment she enters the
          PO — not a settings screen. */}
      <AlertDialog
        open={pendingWriteBack !== null}
        onOpenChange={(open) => {
          if (!open) {
            // Dismissing without a choice (backdrop/Escape) = decline (AC4b)
            // but the invoice edit itself still saves — only the write-back
            // is skipped.
            if (pendingWriteBack) {
              save(pendingWriteBack.value, false);
            }
            setPendingWriteBack(null);
            setIsEditing(false);
          }
        }}
      >
        <AlertDialogContent data-testid="po-write-back-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {projectPoValue ? "Update this project's PO?" : 'Apply this PO to the project too?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {projectPoValue ? (
                <>
                  This project&apos;s PO is {projectPoValue}. Update it to{' '}
                  {pendingWriteBack?.value} for future invoices, or use{' '}
                  {pendingWriteBack?.value} on just this invoice?
                </>
              ) : (
                <>
                  This project doesn&apos;t have a default PO yet. Applying it means every
                  future invoice for {invoice.project_name || 'this project'} will start
                  with {pendingWriteBack?.value}. Skipping saves it on just this invoice.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="po-write-back-skip"
              disabled={updateInvoiceInline.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (pendingWriteBack) save(pendingWriteBack.value, false);
                setPendingWriteBack(null);
                setIsEditing(false);
              }}
            >
              Just this invoice
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="po-write-back-apply"
              disabled={updateInvoiceInline.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (pendingWriteBack) save(pendingWriteBack.value, true);
                setPendingWriteBack(null);
                setIsEditing(false);
              }}
            >
              {projectPoValue ? 'Update project PO' : 'Apply to project'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
